import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const DEFAULT_BACKGROUND = "#0b1628";

export type DownloadElementPdfOptions = {
  backgroundColor?: string;
  /** Device-pixel ratio for sharper captures. */
  scale?: number;
};

type StyleBackup = {
  el: HTMLElement;
  cssText: string;
};

/**
 * Capture a live DOM node and download it as a PDF that matches the on-screen report.
 */
export async function downloadElementAsPdf(
  element: HTMLElement,
  filename: string,
  options: DownloadElementPdfOptions = {},
): Promise<void> {
  if (typeof document !== "undefined" && document.fonts?.ready) {
    await document.fonts.ready;
  }

  const backgroundColor = options.backgroundColor ?? DEFAULT_BACKGROUND;
  const scale = options.scale ?? 2;

  element.scrollIntoView({ block: "start", behavior: "auto" });

  const backups = prepareLiveElementForCapture(element, backgroundColor);

  try {
    // Let style/layout settle before rasterizing.
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    const canvas = await html2canvas(element, {
      backgroundColor,
      scale,
      useCORS: true,
      allowTaint: true,
      logging: false,
      scrollX: 0,
      scrollY: -window.scrollY,
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight,
      onclone: (_doc, cloned) => {
        prepareClonedReport(cloned, backgroundColor);
      },
    });

    if (canvas.width < 2 || canvas.height < 2) {
      throw new Error("Report snapshot was empty");
    }

    const imgData = canvas.toDataURL("image/png", 1.0);
    await saveCanvasPngAsPdf(imgData, canvas.width, canvas.height, filename);
  } finally {
    restoreStyles(backups);
  }
}

function prepareLiveElementForCapture(
  element: HTMLElement,
  backgroundColor: string,
): StyleBackup[] {
  const backups: StyleBackup[] = [];

  const remember = (el: HTMLElement) => {
    backups.push({ el, cssText: el.style.cssText });
  };

  // Unlock clipping ancestors that can blank/crop html2canvas output.
  let ancestor: HTMLElement | null = element.parentElement;
  while (ancestor) {
    const style = getComputedStyle(ancestor);
    if (
      style.overflow === "hidden" ||
      style.overflowX === "hidden" ||
      style.overflowY === "hidden"
    ) {
      remember(ancestor);
      ancestor.style.overflow = "visible";
      ancestor.style.overflowX = "visible";
      ancestor.style.overflowY = "visible";
    }
    ancestor = ancestor.parentElement;
  }

  remember(element);
  element.style.backgroundColor = backgroundColor;
  element.style.padding = "32px";
  element.style.boxSizing = "border-box";
  element.style.overflow = "visible";
  element.style.width = "100%";
  element.style.maxWidth = "890px";

  return backups;
}

function prepareClonedReport(cloned: HTMLElement, backgroundColor: string) {
  cloned.style.backgroundColor = backgroundColor;
  cloned.style.overflow = "visible";
  cloned.style.width = `${Math.max(cloned.scrollWidth, 890)}px`;
  cloned.style.maxWidth = "none";
  cloned.style.boxSizing = "border-box";

  // backdrop-filter often renders blank in canvas clones
  cloned.querySelectorAll("*").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.style.backdropFilter = "none";
    // Keep vendor-prefixed clearing without relying on CSSStyleDeclaration indexing.
    node.style.setProperty("-webkit-backdrop-filter", "none");
    node.style.overflow = "visible";
  });

  cloned.querySelectorAll("dd").forEach((dd) => {
    const el = dd as HTMLElement;
    el.style.maxWidth = "none";
    el.style.whiteSpace = "nowrap";
  });
}

function restoreStyles(backups: StyleBackup[]) {
  // Restore in reverse order.
  for (let i = backups.length - 1; i >= 0; i -= 1) {
    const item = backups[i];
    if (!item) continue;
    item.el.style.cssText = item.cssText;
  }
}

async function saveCanvasPngAsPdf(
  imgData: string,
  canvasWidth: number,
  canvasHeight: number,
  filename: string,
): Promise<void> {
  // Fit onto A4 pages while preserving full width (no horizontal crop).
  const pdf = new jsPDF({
    orientation: canvasWidth >= canvasHeight ? "landscape" : "portrait",
    unit: "pt",
    format: "a4",
    compress: true,
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const usableWidth = pageWidth - margin * 2;
  const usableHeight = pageHeight - margin * 2;

  const imgWidth = usableWidth;
  const imgHeight = (canvasHeight * imgWidth) / canvasWidth;

  if (imgHeight <= usableHeight) {
    pdf.addImage(
      imgData,
      "PNG",
      margin,
      margin,
      imgWidth,
      imgHeight,
      undefined,
      "FAST",
    );
  } else {
    // Paginate vertically from the full-width bitmap.
    const pageCanvas = document.createElement("canvas");
    const pageCtx = pageCanvas.getContext("2d");
    if (!pageCtx) throw new Error("Unable to create PDF page canvas");

    const pxPerPt = canvasWidth / imgWidth;
    const pageSlicePx = Math.floor(usableHeight * pxPerPt);
    let sourceY = 0;
    let pageIndex = 0;

    // Load the full image once for slicing.
    const fullImage = await loadImage(imgData);

    while (sourceY < canvasHeight) {
      if (pageIndex > 0) pdf.addPage();

      const slicePx = Math.min(pageSlicePx, canvasHeight - sourceY);
      pageCanvas.width = canvasWidth;
      pageCanvas.height = Math.max(1, slicePx);

      pageCtx.fillStyle = DEFAULT_BACKGROUND;
      pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      pageCtx.drawImage(
        fullImage,
        0,
        sourceY,
        canvasWidth,
        slicePx,
        0,
        0,
        canvasWidth,
        slicePx,
      );

      const sliceData = pageCanvas.toDataURL("image/png", 1.0);
      const sliceHeightPt = slicePx / pxPerPt;

      pdf.addImage(
        sliceData,
        "PNG",
        margin,
        margin,
        imgWidth,
        sliceHeightPt,
        undefined,
        "FAST",
      );

      sourceY += slicePx;
      pageIndex += 1;
    }
  }

  const pdfName = filename.endsWith(".pdf") ? filename : `${filename}.pdf`;
  pdf.save(pdfName);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load report snapshot"));
    image.src = src;
  });
}

export function buildTciReportPdfFilename(
  workflowId: string,
  date = new Date(),
): string {
  const stamp = date.toISOString().slice(0, 10);
  return `tci-report-${workflowId}-${stamp}.pdf`;
}
